import {
  FILTER_OPERATOR_ARITY,
  type Filter,
  type FilterOperator,
  type Interaction,
  type ScalarValue,
  type Selection,
  type TimeGrain,
} from '@aibi/contracts';
import { typeFamily, type SemanticModelIndex } from '@aibi/semantic-model';
import { DiagnosticBag, type AnalysisDiagnostic } from './diagnostics.js';
import {
  grainSupportsType,
  measureDataType,
  NON_COMPARABLE_TYPES,
  valueMatchesType,
} from './field-types.js';
import type { PlannedFilter, PlannedFilterTarget } from './query-plan.js';
import type { DrilldownPathFilter } from './resolve-drilldown.js';

export interface FilterResolutionInput {
  readonly filters: readonly Filter[];
  readonly selections: readonly Selection[];
  readonly selectionMode: Interaction['selectionMode'];
  readonly pathFilters: readonly DrilldownPathFilter[];
  /** `measureId` -> `key` de las medidas presentes en el analisis. */
  readonly measureKeys: ReadonlyMap<string, string>;
}

export interface FilterResolution {
  readonly filters: readonly PlannedFilter[];
  readonly diagnostics: readonly AnalysisDiagnostic[];
}

const TEXT_OPERATORS: ReadonlySet<FilterOperator> = new Set([
  'contains',
  'starts-with',
  'ends-with',
]);
const RANGE_OPERATORS: ReadonlySet<FilterOperator> = new Set(['gt', 'gte', 'lt', 'lte', 'between']);
const NULL_OPERATORS: ReadonlySet<FilterOperator> = new Set(['is-null', 'is-not-null']);

/**
 * Convierte filtros del estado, selecciones y rutas de drilldown en filtros
 * estructurados del plan, validando referencias, aridad, compatibilidad operador/tipo y
 * valor/tipo. Los filtros sobre medidas se aplican despues de agregar. No genera SQL.
 *
 * @example
 * resolveFilters({ filters, selections: [], selectionMode: 'multiple', pathFilters: [], measureKeys }, index);
 */
export function resolveFilters(
  input: FilterResolutionInput,
  index: SemanticModelIndex,
): FilterResolution {
  const bag = new DiagnosticBag();
  const filters: PlannedFilter[] = [];

  const dimensionTarget = (
    dimensionId: string,
    timeGrain: TimeGrain | null,
    path: string,
  ): PlannedFilterTarget | undefined => {
    const resolved = index.resolveDimension(dimensionId);
    if (!resolved) {
      bag.error('missing-reference', `Dimension inexistente "${dimensionId}".`, { path });
      return undefined;
    }
    if (timeGrain !== null && !grainSupportsType(timeGrain, resolved.dataType)) {
      bag.error(
        'invalid-type',
        `El grano "${timeGrain}" no es aplicable a una dimension ${resolved.dataType}.`,
        { path },
      );
      return undefined;
    }
    return {
      kind: 'dimension',
      dimensionId,
      tableId: resolved.table.id,
      columnId: resolved.column?.id ?? null,
      timeGrain,
      dataType: resolved.dataType,
    };
  };

  const add = (
    filter: Omit<PlannedFilter, 'stage' | 'includeNull'> & { includeNull?: boolean },
    path: string,
  ): void => {
    if (!checkFilter(filter.target, filter.operator, filter.values, path, bag)) return;
    filters.push({
      id: filter.id,
      origin: filter.origin,
      stage: filter.target.kind === 'measure' ? 'aggregate' : 'row',
      target: filter.target,
      operator: filter.operator,
      values: filter.values,
      includeNull: filter.includeNull ?? false,
    });
  };

  input.filters.forEach((filter, i) => {
    const path = `filters.${i}`;
    let target: PlannedFilterTarget | undefined;
    if (filter.target.kind === 'dimension') {
      target = dimensionTarget(
        filter.target.dimensionId,
        filter.target.timeGrain ?? null,
        `${path}.target`,
      );
    } else {
      const { measureId } = filter.target;
      const measure = index.getMeasure(measureId);
      if (!measure) {
        bag.error('missing-reference', `Medida inexistente "${measureId}".`, {
          path: `${path}.target`,
        });
      } else {
        target = {
          kind: 'measure',
          measureId,
          measureKey: input.measureKeys.get(measureId) ?? null,
          dataType: measureDataType(measure, index),
        };
      }
    }
    if (target) {
      add(
        {
          id: filter.id,
          origin: 'state',
          target,
          operator: filter.operator,
          values: filter.values,
        },
        path,
      );
    }
  });

  input.selections.forEach((selection, i) => {
    const path = `selections.${i}`;
    if (input.selectionMode === 'single' && selection.values.length > 1) {
      bag.error(
        'invalid-filter',
        'El modo de seleccion "single" no admite varios valores seleccionados.',
        { path: `${path}.values` },
      );
      return;
    }
    const target = dimensionTarget(selection.dimensionId, null, `${path}.dimensionId`);
    if (!target) return;
    const values = selection.values.filter((value) => value !== null);
    const includeNull = values.length < selection.values.length;
    const id = `@selection.${i}`;
    if (values.length === 0) {
      add({ id, origin: 'selection', target, operator: 'is-null', values: [] }, path);
    } else {
      add(
        {
          id,
          origin: 'selection',
          target,
          operator: values.length === 1 ? 'eq' : 'in',
          values,
          includeNull,
        },
        path,
      );
    }
  });

  for (const pathFilter of input.pathFilters) {
    const target = dimensionTarget(pathFilter.dimensionId, null, pathFilter.path);
    if (!target) continue;
    add(
      {
        id: pathFilter.id,
        origin: 'drilldown',
        target,
        operator: pathFilter.value === null ? 'is-null' : 'eq',
        values: pathFilter.value === null ? [] : [pathFilter.value],
      },
      pathFilter.path,
    );
  }

  return { filters, diagnostics: bag.items };
}

/** Valida aridad, operador frente al tipo y cada valor frente al tipo. */
function checkFilter(
  target: PlannedFilterTarget,
  operator: FilterOperator,
  values: readonly ScalarValue[],
  path: string,
  bag: DiagnosticBag,
): boolean {
  const { dataType } = target;
  const grain = target.kind === 'dimension' ? target.timeGrain : null;
  const family = grain === null ? typeFamily(dataType) : 'grain';
  const before = bag.items.length;
  const fail = (code: 'invalid-filter' | 'invalid-type', message: string, at = path): void => {
    bag.error(code, message, { path: at });
  };

  const [min, max] = FILTER_OPERATOR_ARITY[operator];
  if (values.length < min || values.length > max) {
    fail('invalid-filter', `El operador "${operator}" no admite ${values.length} valores.`);
  }
  if (TEXT_OPERATORS.has(operator) && family !== 'text' && family !== 'unknown') {
    fail('invalid-filter', `El operador "${operator}" solo aplica a texto (recibe ${dataType}).`);
  }
  if (
    RANGE_OPERATORS.has(operator) &&
    (family === 'boolean' || (grain === null && NON_COMPARABLE_TYPES.has(dataType)))
  ) {
    fail('invalid-filter', `El operador "${operator}" requiere un tipo ordenable (${dataType}).`);
  }
  if (!NULL_OPERATORS.has(operator) && grain === null && NON_COMPARABLE_TYPES.has(dataType)) {
    fail('invalid-filter', `No se puede filtrar por valor un campo ${dataType}.`);
  }

  values.forEach((value, i) => {
    const at = `${path}.values.${i}`;
    if (value === null) {
      fail('invalid-filter', 'Use "is-null" o "is-not-null" para comparar con nulos.', at);
    } else if (!valueMatchesType(value, dataType, grain)) {
      fail(
        'invalid-type',
        `El valor ${i + 1} (${typeof value}) no es compatible con ${grain === null ? dataType : `el grano ${grain}`}.`,
        at,
      );
    }
  });

  if (operator === 'between' && boundsReversed(values[0], values[1])) {
    fail('invalid-filter', 'En "between" el limite inferior no puede superar al superior.');
  }

  return bag.items.length === before;
}

function boundsReversed(low: ScalarValue | undefined, high: ScalarValue | undefined): boolean {
  if (typeof low === 'number' && typeof high === 'number') return low > high;
  if (typeof low === 'string' && typeof high === 'string') return low > high;
  return false;
}
